<template>
  <div v-if="isVisible" class="whatsapp-bubble-container">
    <!-- Message Bubble -->
    <div class="message-bubble">
      <!-- Header with Background and Logo -->
      <div class="bubble-header">
        <button @click="closeBubble" class="close-button">
          <X :size="16" />
        </button>
        
        <div class="header-logo-container">
          <img :src="communityLogo" alt="Peelo Community" class="header-logo" />
        </div>
        <div class="header-text">
          <h3 class="community-name">{{ communityName }}</h3>
          <p class="community-subtitle">{{ communitySubtitle }}</p>
        </div>
      </div>

      <!-- Content -->
      <div class="bubble-content">
        <div class="message-card">
          <img :src="groupLogo" alt="Group" class="group-logo" />
          <div class="message-text-container">
            <p class="message-text">
              <strong>{{ groupName }}</strong> — {{ displayMessage }}
            </p>
            <div class="message-time">
              {{ time }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Button -->
    <a 
      :href="link"
      target="_blank"
      rel="noopener noreferrer"
      class="whatsapp-button"
    >
      <MessageCircle :size="32" />
      <!-- Notification Badge -->
      <span class="notification-badge-container">
        <span class="ping-animation"></span>
        <span class="notification-badge">1</span>
      </span>
    </a>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { X, MessageCircle } from 'lucide-vue-next';

const { t } = useI18n();

const props = defineProps({
  communityName: {
    type: String,
    default: 'Peelo Chat Community'
  },
  communityLogo: {
    type: String,
    default: '/assets/community-logo.jpg' // Ensure this asset exists or provide a placeholder
  },
  groupName: {
    type: String,
    default: 'Console API'
  },
  groupLogo: {
    type: String,
    default: '/assets/group-logo.png' // Ensure this asset exists or provide a placeholder
  },
  message: {
    type: String,
    default: null // Will use translated default
  },
  time: {
    type: String,
    default: '12:42 PM'
  },
  link: {
    type: String,
    default: 'https://chat.whatsapp.com/IVTd8HYpflj5hfN7CO68Bc'
  }
});

const isVisible = ref(false);

onMounted(() => {
  // Check if user has previously dismissed the bubble
  const isDismissed = localStorage.getItem('hideWhatsAppBubble') === 'true';
  if (!isDismissed) {
    isVisible.value = true;
  }
});

// Computed for translated subtitle
const communitySubtitle = computed(() => t('whatsappBubble.officialCommunity'));

// Computed for message with fallback to translation
const displayMessage = computed(() => props.message || t('whatsappBubble.defaultMessage'));

const closeBubble = () => {
  isVisible.value = false;
  localStorage.setItem('hideWhatsAppBubble', 'true');
};
</script>

<style scoped>
.whatsapp-bubble-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 50;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  animation: slideIn 0.5s ease-out;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes ping {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

/* Message Bubble Styles */
.message-bubble {
  background-color: var(--bg-white, #fff);
  color: #1e293b; /* slate-800 */
  border-radius: 8px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 320px; /* max-w-xs */
  border: 1px solid #e2e8f0; /* slate-200 */
  position: relative;
  margin-bottom: 8px;
  overflow: hidden;
}

/* Header Styles */
.bubble-header {
  background-color: #075E54;
  padding: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

.close-button {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  color: #fff;
}

.header-logo-container {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #fff;
  padding: 2px;
  overflow: hidden;
  flex-shrink: 0;
}

.header-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.header-text {
  display: flex;
  flex-direction: column;
}

.community-name {
  color: #fff;
  font-weight: 700;
  font-size: 14px;
  margin: 0;
  line-height: 1.2;
}

.community-subtitle {
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  margin: 0;
}

/* Content Styles */
.bubble-content {
  padding: 16px;
  background-color: rgba(229, 221, 213, 0.3); /* #E5DDD5 with opacity */
}

.message-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background-color: #fff;
  padding: 12px;
  border-radius: 8px;
  border-top-left-radius: 0;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9; /* slate-100 */
}

.group-logo {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.message-text-container {
  flex: 1;
}

.message-text {
  font-size: 12px;
  line-height: 1.6;
  color: #334155; /* slate-700 */
  margin: 0;
}

.message-time {
  margin-top: 8px;
  font-size: 10px;
  color: #94a3b8; /* slate-400 */
  font-family: monospace;
  text-align: right;
}

/* Button Styles */
.whatsapp-button {
  background-color: #25D366;
  color: #fff;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: transform 0.2s, background-color 0.2s;
  text-decoration: none;
}

.whatsapp-button:hover {
  background-color: #20bd5a;
  transform: scale(1.1);
}

.notification-badge-container {
  position: absolute;
  top: -4px;
  right: -4px;
  display: flex;
  height: 16px;
  width: 16px;
}

.ping-animation {
  animation: ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
  position: absolute;
  display: block;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  border-radius: 50%;
  background-color: #f87171; /* red-400 */
  opacity: 0.75;
}

.notification-badge {
  position: relative;
  display: inline-flex;
  border-radius: 50%;
  height: 16px;
  width: 16px;
  background-color: #ef4444; /* red-500 */
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  align-items: center;
  justify-content: center;
}
</style>
